import axiosInstance from '../fetchConfig/axiosConfig';
import { toast } from '../common/toast';
import dayjs from 'dayjs';
import { getEvaluationDate, calculateUnifiedDateRange } from '../common/dateUtils';
import { usePersistedDataStore } from '../components/store';
import { mapRuntype } from '../utils/mapRuntype';
import { getAVUtil, getIFMVUtil } from '../utils/getAVUtil';
import logger from '../utils/logger';
import { clearPersistedInventoryUpdates } from '../components/CaseReview/CaseReviewHelpers/persistedInventoryUpdates';
// import { isCaseSettled } from '../components/CaseReview/SummaryCaseTable/utils/helpers';

// Configuration for run types that should skip warnings
const RUN_TYPES_SKIP_WARNINGS = ['data_analysis', 'settlement'];

// Threshold for displaying large query warnings
const CLIENT_COUNT_WARNING_THRESHOLD = 1000;

export const syncCaseNotesFromCases = (cases) => {
  usePersistedDataStore.getState().setCaseNotes(
    Array.isArray(cases) ? cases.map((caseItem) => caseItem?.CaseNotes ?? null) : []
  );
};


/**
 * Checks if warnings should be displayed based on run type and configuration
 * @param {string} reportType - The current report type
 * @param {boolean} skipLargeQueryWarning - Whether to skip large query warnings
 * @returns {boolean} - Whether warnings should be displayed
 */
const shouldDisplayWarnings = (reportType, skipLargeQueryWarning = false) => {
  // Skip warnings for configured run types
  if (RUN_TYPES_SKIP_WARNINGS.includes(reportType)) {
    return false;
  }
  
  // Skip warnings if explicitly requested
  if (skipLargeQueryWarning) {
    return false;
  }
  
  return true;
};

/**
 * Build run parameters with proper defaults and overrides
 * @param {Object} options - Configuration options
 * @param {Object} options.locationParams - Location parameters
 * @param {Object} options.user - User object
 * @param {Object} options.overrides - Parameter overrides
 * @param {Object} options.state - Current component state
 * @returns {Object} - Built parameters object
 */
export const buildRunParams = ({
  locationParams,
  user,
  overrides = {},
  state = {}
}) => {



  const {
    MuniCode: overrideMuniCode,
    TaxYear: overrideTaxYear,
    county: overrideCounty,
    municipality: overrideMunicipality,
    village: overrideVillage,
    propertyType: overridePropertyType,
    reportType: overrideReportType,
    UserId: overrideUserId,
    TrainingUser: overrideTrainingUser,
    CourtDate: overrideCourtDate,
    additionalInput,
    runTypeOverride,
    uids,
    overwriteComps,
    expandComps,
    compLimit,
    showSettledCases,
    scarOnly,
    unreviewed_flag,
    manualReview,
    mlsComps,
    allowConditionIssues,
    reviewedCasesFlag,
    repID,
    // Comp filtering parameters
    squareFeetFilter,
    acreageFilter,
    grossAdjPctFilter,
    netAdjPctFilter,
    compFilters,
    dateRange,
    dateRangeType,
    beforePreset,
    afterPreset,
    evalDate,
    ...rest
  } = overrides;



  const MuniCode = overrideMuniCode || locationParams.MuniCode;
  const TaxYear = overrideTaxYear || locationParams.TaxYear;
  const county = overrideCounty || locationParams.county;
  

  const municipality = overrideMunicipality || locationParams.municipality;
  const village = overrideVillage || locationParams.village;
  const propertyTypeFinal = overridePropertyType || state.propertyType;
  const reportTypeFinal = overrideReportType || state.reportType;
  const TrainingUser = overrideTrainingUser ?? (user?.userAccess?.level === 'Training' ? 1 : 0);
  // unnecessarily safe but fine I guess
  const UserId = overrideUserId !== undefined ? overrideUserId : user?.userId;
  
  // Handle CourtDate specially - prioritize override, then locationParams, then state
  const CourtDate = overrideCourtDate !== undefined ? overrideCourtDate : 
                   locationParams.CourtDate || state.courtDate;
  const CourtRun = CourtDate ? 1 : 0;

  const formattedCourtDate = CourtDate
    ? (() => {
        // Check if the date is already in YYYY-MM-DD format
        if (CourtDate.includes('-')) {
          return CourtDate; // Already in correct format
        }
        // Format from MM/DD/YYYY to YYYY-MM-DD
        const [month, day, year] = CourtDate.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      })()
    : null;

  const badDataFields = state.badDataFlags?.data || [];
  const outlierFields = state.badDataFlags?.outliers || [];
  const villageRun =
    (village && village !== 'All') || (MuniCode && MuniCode.length > 3 && TrainingUser !== 1)
      ? 1
      : 0;

  const baseParams = {
    MuniCode,
    TaxYear,
    county,
    municipality,
    village,
    villageRun,
    propertyType: propertyTypeFinal,
    reportType: villageRun ? 'village' : reportTypeFinal,
    UserId,
    TrainingUser,
    BadDataFields: badDataFields,
    OutlierFields: outlierFields,
    CourtDate: formattedCourtDate,
    CourtRun,
    runType: runTypeOverride || state.runType,
    overwriteComps,
    expandComps,
    compLimit,
    showSettledCases,
    scarOnly,
    unreviewed_flag: unreviewed_flag ?? 'False',
    manualReview,
    mlsComps,
    allowConditionIssues,
    reviewedCasesFlag,
    repID,
    // Comp filtering parameters
    squareFeetFilter,
    acreageFilter,
    grossAdjPctFilter,
    netAdjPctFilter,
    compFilters,
    dateRange,
    dateRangeType,
    beforePreset,
    afterPreset,
    evalDate,
    ...(uids && { uids }),
    ...rest,
  };



  // Handle additionalInput similarly - only use override if explicitly provided (not undefined)
  const finalAdditionalInput = additionalInput !== undefined ? additionalInput : locationParams.additionalInput;
  

  
  // If we have additional input (single parcel ID), set the overrideIds array
  if (finalAdditionalInput) {
    const pid = finalAdditionalInput;
    return {
      ...baseParams,
      overrideIds: [pid], // Use existing overrideIds if provided, otherwise create from additionalInput
      additionalInput: finalAdditionalInput, // Preserve additionalInput for validation
      MuniCode: pid.slice(0, 3),
      county: pid.slice(0, 1),
      municipality: pid.slice(1, 3),
      // Preserve CourtDate and other important fields from baseParams
      CourtDate: baseParams.CourtDate,
      CourtRun: baseParams.CourtRun,
    };
  }

  return baseParams;
};

/**
 * Custom error class for no clients found after filtering
 */
export class NoClientsFoundError extends Error {
  constructor(message, filterDescription) {
    super(message);
    this.name = 'NoClientsFoundError';
    this.filterDescription = filterDescription;
  }
}

const getParcelIdFromCase = (caseItem) =>
  caseItem?.parcel_id || caseItem?.ParcelID || caseItem?.ParcelId || caseItem?.PID;

const getCaseLabel = (caseItem) => {
  const address = caseItem?.Address || caseItem?.address;
  const parcelId = getParcelIdFromCase(caseItem);
  if (address && parcelId) return `${address} (${parcelId})`;
  return address || parcelId || 'Unknown property';
};

const isMissingNumericValue = (value) =>
  value == null || value === '' || (typeof value === 'number' && !Number.isFinite(value));

/**
 * Reorder API case rows to match the uid list order (used when HighTaxRun is set).
 */
export function orderEnrichedCasesByUids(cases, uids) {
  if (!Array.isArray(cases) || !Array.isArray(uids) || uids.length === 0) {
    return cases;
  }
  const getPid = (caseItem) => {
    const p = getParcelIdFromCase(caseItem);
    return p == null ? null : String(p);
  };
  const caseMap = new Map();
  for (const c of cases) {
    const pid = getPid(c);
    if (pid) caseMap.set(pid, c);
  }
  const uidSet = new Set(uids.map((u) => String(u)));
  const ordered = uids.map((uid) => caseMap.get(String(uid))).filter(Boolean);
  const remaining = cases.filter((c) => {
    const pid = getPid(c);
    return pid && !uidSet.has(pid);
  });
  return remaining.length ? [...ordered, ...remaining] : ordered;
}

function throwIfNoClientIdsForNegotiationObj(updateNegotiationObj, clientIds, { highTax = false } = {}) {
  if (clientIds && clientIds.length > 0) {
    return;
  }
  const activeLabels = [];
  if (updateNegotiationObj?.showSettledCases === false) {
    activeLabels.push('Unsettled');
  }
  if (updateNegotiationObj?.scarOnly) {
    activeLabels.push('Scar Filed');
  }
  if (updateNegotiationObj?.unreviewed_flag && updateNegotiationObj.unreviewed_flag !== 'False') {
    activeLabels.push('Unreviewed');
  }

  const hts = highTax ? ' for high tax run' : '';
  let errorMessage;
  if (activeLabels.length === 0) {
    errorMessage = highTax
      ? 'No clients found for the specified high tax criteria.'
      : 'No clients found for the specified criteria.';
  } else if (activeLabels.length === 1) {
    errorMessage = `No ${activeLabels[0]} cases found${hts}`;
  } else {
    const lowerLabels = activeLabels.map((label) => label.toLowerCase());
    const listText = lowerLabels.length === 2
      ? `${lowerLabels[0]} and ${lowerLabels[1]}`
      : `${lowerLabels.slice(0, -1).join(', ')} and ${lowerLabels[lowerLabels.length - 1]}`;
    errorMessage = `No ${listText} cases found${hts}.`;
  }
  throw new NoClientsFoundError(errorMessage, '');
}

// Simplified load client ids to either hit new high tax load, or the standard.
// really the load_client_ids should handle this with flags to keep things cleaner but whatever.
export const newLoadClientIds = async (updateNegotiationObj) => {
  const highTax = updateNegotiationObj?.HighTaxRun === true;
  const path = highTax ? '/load_high_tax_client_ids' : '/load_client_ids';
  logger.log('updateNegotiationObj going into newLoadClientIds', updateNegotiationObj, path);
  const response = await axiosInstance.post(path, updateNegotiationObj);
  const clientIds = response.data?.client_ids;
  throwIfNoClientIdsForNegotiationObj(updateNegotiationObj, clientIds, { highTax });
  return response.data;
};

/**
 * Clean and simplified initialize fetch utility
 * @param {Object} options - Configuration options
 * @param {Object} options.locationParams - Location parameters
 * @param {Object} options.user - User object
 * @param {Object} options.overrides - Parameter overrides
 * @param {Object} options.state - Current component state
 * @param {Object} options.advancedSettings - Advanced settings for date calculations
 * @param {Object} options.options - Additional options
 * @returns {Promise<Object>} - Result object with success status and data
 */
export const initializeFetch = async ({
  locationParams,
  user,
  overrides = {},
  state = {},
  advancedSettings = {},
  options = {}
}) => {

  // Create a deep copy of advancedSettings to prevent modifications
  const advancedSettingsCopy = JSON.parse(JSON.stringify(advancedSettings));
  const {
    skipLargeQueryWarning = false,
    uids = null,
    isTownView = false
  } = options;

  try {
    clearPersistedInventoryUpdates();
    usePersistedDataStore.getState().setCaseNotes([]);

    // Build parameters using the utility function
    // If locationParams already contains built parameters (like MuniCode, TaxYear, overrideIds), use them directly
    let params = 
      buildRunParams({
        locationParams,
        user,
        overrides,
        state
      });
    
    // For single parcel runs, ensure we extract the municipality code from the parcel ID
    if (params.additionalInput) {
      const pid = params.additionalInput;
      params.overrideIds = [pid];
      params.MuniCode = pid.slice(0, 3);
      params.county = pid.slice(0, 1);
      params.municipality = pid.slice(1, 3);
    }
    
    // Ensure court date is properly formatted regardless of how params were built
    if (params.CourtDate && params.CourtDate.includes('/')) {
      const [month, day, year] = params.CourtDate.split('/');
      params.CourtDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Ensure CourtRun is properly set based on CourtDate presence
    params.CourtRun = params.CourtDate ? 1 : 0;
    
    // UserId should already be set in params from the calling function 

    const {
      MuniCode,
      TaxYear,
      CourtDate: courtDate,
      overwriteComps,
      expandComps,
      compLimit,
      village,
      reportType,
      showSettledCases,
      manualReview,
      mlsComps,
      allowConditionIssues,
      scarOnly,
      reviewedCasesFlag,
      unreviewed_flag,
      repID,
      overrideIds,
      UserId,
      // Comp filtering parameters
      squareFeetFilter,
      acreageFilter,
      grossAdjPctFilter,
      netAdjPctFilter,
      compFilters,
      dateRangeType,
      beforePreset,
      afterPreset,
    } = params;

    if (reportType === 'data_analysis') {
      // clear out the prior data analysis result
      const z = usePersistedDataStore.getState();
      z.setDataAnalysisResult([]);
      z.setDataAnalysisColumns([]);
      z.setDataAnalysisFilterModel(null);
    }

    // Get cutoff dates from the calculated date range (for CaseAnalysisContainer)
    const fallbackYear = TaxYear || dayjs().year() - 1;
    let currentEvalDate = dayjs(`${fallbackYear - 1}-07-01`).format('YYYY-MM-DD');
    let earliestCutoffDate = null;
    let latestCutoffDate = null;
    let currentDateRange = advancedSettingsCopy?.dateRange;
    
    try {
      // Fetch the actual evaluation date from the backend
      currentEvalDate = await getEvaluationDate(MuniCode, TaxYear);
      // get (and set) the RAR and eq values 
    } catch (error) {
      logger.error('Error fetching evaluation date:', error);

    }

    // set the date ranges when not in town view
    if (!isTownView) {
      // Handle date range calculation based on type - use values from overrides (passed from CaseAnalysisContainer)
      if (overrides.dateRangeType === 'preset') {
        const beforePreset = overrides.beforePreset || '12m';
        const afterPreset = overrides.afterPreset || '12m';
        
        // Use the same logic as AdvancedSettings to calculate date range
        if (currentEvalDate) {
          // Handle "nocutoff" and "nolimit" values
          const effectiveBeforePreset = beforePreset === 'nocutoff' ? null : beforePreset;
          const effectiveAfterPreset = afterPreset === 'nolimit' ? null : afterPreset;
          
          // Use the unified date range calculation
          currentDateRange = calculateUnifiedDateRange(
            currentEvalDate,
            effectiveBeforePreset,
            effectiveAfterPreset
          );
        }
      } else if (overrides.dateRangeType === 'custom' && overrides.dateRange) {
        // For custom date ranges, use the provided dates directly
        currentDateRange = overrides.dateRange;
        logger.log('initializeFetch - using custom dateRange:', currentDateRange);
      }

      earliestCutoffDate = currentDateRange?.[0];
      latestCutoffDate = currentDateRange?.[1];
      

    }

    // Get the current search params to extract the string values
    const searchParams = new URLSearchParams(window.location.search);
    const countyString = searchParams.get('countyString') || 'All';
    let muniString = searchParams.get('municipalityString') || 'All';
    
    // If in village mode, use village name instead of municipality name
    const villageString = searchParams.get('villageString');
    if (villageString && villageString !== 'All' && villageString !== '') {
      muniString = villageString;
    }
    
    // Only append comp=1 for non-town views
    if (!isTownView && (!searchParams.has('comp') || searchParams.get('comp') !== '1')) {
      searchParams.set('comp', '1');
      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }

    // This is stupid and we should just reference the raw object directly from store. However, in case this is transfomring any data leaving for now.
    // Create negotiation object
    let updateNegotiationObj = {
      MuniCode,
      TaxYear,
      runType: params.runType || 'all_clients',
      countyString,
      muniString,
      CourtDate: courtDate, // Already formatted from buildRunParams
      CourtRun: params.CourtRun || 0,
      Stage: advancedSettingsCopy?.stage,
      showSettledCases,
      manualReview,
      mlsCompsOnly: mlsComps,
      allowConditionIssues,
      earliestCutoffDate,
      latestCutoffDate,
      reportType: reportType,
      villageRun: params.villageRun ?? 0,
      TrainingUser: user?.userAccess?.level === 'Training' ? 1 : 0,
      UserId: UserId,
      manualReviewOverride: overwriteComps || 0,
      expandComps: expandComps === true,
      compLimit: compLimit != null && compLimit !== '' ? compLimit : null,
      scarOnly,
      reviewedCasesFlag,
      unreviewed_flag,
      ...(repID ? { RepID: repID } : {}),
      HighTaxRun: advancedSettingsCopy?.highTaxRun === true,
      skipRerunOnInventoryUpdate: advancedSettingsCopy?.skipRerunOnInventoryUpdate === true,
      HighTaxCount:
        advancedSettingsCopy?.highTaxRun === true
          ? (advancedSettingsCopy?.highTaxCount == null || advancedSettingsCopy?.highTaxCount === ''
              ? null
              : advancedSettingsCopy.highTaxCount)
          : null,
      timeAdjustmentMonths: (() => {
        const raw = advancedSettingsCopy?.timeAdjustmentMonths;
        if (raw != null && raw !== '') {
          const n = Math.floor(Number(raw));
          if (Number.isFinite(n) && n >= 1) return n;
        }
        return advancedSettingsCopy?.useTimeAdjustedPrice === true ? 3 : null;
      })(),
      ...(overrideIds && overrideIds.length > 0 ? { overrideIds } : {}),
    };
    


    // Add comp filtering parameters and evaluation date - this shouild be grouped in the above conditional.
    if (!isTownView) {
      // Use the calculated date range from earlier in the function
      const finalDateRange = currentDateRange;
      const finalEarliestCutoff = earliestCutoffDate;
      const finalLatestCutoff = latestCutoffDate;
      
      Object.assign(updateNegotiationObj, {
        squareFeetFilter,
        acreageFilter,
        grossAdjPctFilter,
        netAdjPctFilter,
        compFilters,
        dateRange: finalDateRange,
        dateRangeType,
        beforePreset,
        afterPreset,
        evalDate: currentEvalDate,
        earliestCutoffDate: finalEarliestCutoff,
        latestCutoffDate: finalLatestCutoff,
        // Add these for backward compatibility and to ensure all components can access the same values
        earliestSaleIntDate: finalEarliestCutoff ? Math.abs(dayjs(currentEvalDate).diff(dayjs(finalEarliestCutoff), 'day')) : null,
        latestSaleIntDate: finalLatestCutoff ? Math.abs(dayjs(finalLatestCutoff).diff(dayjs(currentEvalDate), 'day')) : null,
      });
      

      
    } else {
      // For town view, also fetch and set the evaluation date
      try {
        const townEvalDate = await getEvaluationDate(MuniCode, TaxYear);
        updateNegotiationObj.evalDate = townEvalDate;
      } catch (error) {
        logger.error('Error fetching evaluation date for town view:', error);
        const fallbackYear = TaxYear || dayjs().year() - 1;
        updateNegotiationObj.evalDate = dayjs(`${fallbackYear - 1}-07-01`).format('YYYY-MM-DD');
      }
    }

    // Load client IDs (including for single parcel cases via overrideIds)
    // Filtering is now handled inside loadClientIds
    let ClientUids = [];
    let ClientCases = [];
  
    // Detect single parcel run - skip loadClientIds for single parcel runs
    // This will also trigger
    const isSingleParcelRun = params.additionalInput || 
      (overrideIds && overrideIds.length === 1);

    if (isSingleParcelRun) {
      logger.log('⏭️ [INITIALIZE_FETCH] Skipping newLoadClientIds for single parcel run:', {
        overrideIds: overrideIds,
      });
      // Use overrideIds directly as uids for single parcel runs
      ClientUids = overrideIds || [];
      ClientCases = [];
    } else if (reportType === 'marketing') {
      ClientUids = [];
      ClientCases = [];
    } else if (Array.isArray(uids) && uids.length > 0) {
      ClientUids = uids;
      ClientCases = [];
    // STANDARD CASE RESPONSE
    } else {
      const { client_ids } = await newLoadClientIds(updateNegotiationObj);
      
      ClientUids = client_ids;
      ClientCases = [];
    }

    // Large query warning (for CaseAnalysisContainer; town portal loads full municipality by design)
    if (shouldDisplayWarnings(reportType, skipLargeQueryWarning || isTownView) && ClientUids && ClientUids.length > CLIENT_COUNT_WARNING_THRESHOLD) {
      logger.log('showing large query warning for high client count');
      
      // Create the negotiation object with the data we already have
      const negotiationObj = { 
        ...updateNegotiationObj, 
        uids: ClientUids, 
        cases: ClientCases
      };
      
      return { 
        success: false, 
        reason: 'large_query_warning',
        data: { 
          count: ClientUids.length, 
          params,
          negotiationObj,
          cases: ClientCases,
          uids: ClientUids
        }
      };
    }

    // Load adjustments (for CaseAnalysisContainer)
    if (!isTownView) {
      // try {
      //   // const loadAdjustmentsResponse = await axiosInstance.post(`/load_adjustments`, updateNegotiationObj);
      //   // const adjustments = loadAdjustmentsResponse.data.response;
      //   // Note: adjustments are handled by the calling component
      // } catch (error) {
      //   logger.error('Error loading adjustments:', error);
      // }
    }

    // Filtering is now handled inside loadClientIds
    // Cases and UIDs are already filtered at this point
    logger.log('ClientUids', ClientUids);

    // Create final negotiation object
    const updatedNegotiationObj = { 
      ...updateNegotiationObj, 
      uids: ClientUids, 
      cases: ClientCases
    };
    

    return { 
      success: true, 
      data: {
        negotiationObj: updatedNegotiationObj,
        cases: ClientCases,
        uids: ClientUids,
        params: params
      }
    };

  } catch (error) {
    logger.error('Error during fetch:', error);
    
    // Handle NoClientsFoundError specifically
    if (error instanceof NoClientsFoundError) {
      toast.error(error.message, {
        autoClose: false,
        closeOnClick: true,
        draggable: true,
      });
      return { success: false, reason: 'no_clients_found', error: error.message };
    }
    
    return { success: false, reason: 'error', error };
  }
}; 

/**
 * Enrich Client Cases - Fetches enriched case data from /enrich_client_cases
 * Accesses negotiationObj directly from persisted state.
 * For a non-client single-PID run, the API adds RAR/EQ from Calcs.RAR (Flask: attach_municipal_rar_eq_if_missing).
 */
export const enrichClientCases = async ({ validateAvIfmv = false } = {}) => {
  const store = usePersistedDataStore.getState();
  const negotiationObj = store.negotiationObj;
  const setIsEnrichingClientCases = store.setIsEnrichingClientCases;
  const setNegotiationObj = store.setNegotiationObj;

  try {
    const uids = negotiationObj?.uids || [];

    // Set loading state
    setIsEnrichingClientCases(true);
    
    // Check user level if training to override runtype
    const isTrainingUser = negotiationObj?.TrainingUser === 1 ? true : false;
  
    // Its really stupid that we are doing these remappings etc for keynames. but whatever, the others aren't fully uniform anyways.
    const enrichmentParams = {
      CourtDate: negotiationObj.CourtDate,
      municode: negotiationObj.MuniCode,
      courtrunbool: negotiationObj.CourtRun,
      year: negotiationObj.TaxYear,
      runtype: isTrainingUser ? 'Training' : mapRuntype(negotiationObj.reportType),
      userid: negotiationObj.UserId,
      uids: uids,
      // Same semantics as batch save -> /update_comparables (HF._map_run_type + village MuniCode)
      reportType: negotiationObj.reportType || 'standard',
      TrainingFlag: negotiationObj?.TrainingUser === 1 ? 1 : 0,
    };

    const response = await axiosInstance.post('/enrich_client_cases', enrichmentParams);
    const cases = response.data;
    logger.log('Enriched cases', cases);

    const rawCases = Array.isArray(cases) ? cases : [];
    const updatedCases =
      negotiationObj?.HighTaxRun === true
        ? orderEnrichedCasesByUids(rawCases, uids)
        : rawCases;

    const updatedNegotiationObj = {
      ...negotiationObj,
      cases: updatedCases,
      uids: uids,
    };

    setNegotiationObj(updatedNegotiationObj);
    syncCaseNotesFromCases(updatedCases);

    const isSingleParcelRun = negotiationObj?.runType === 'single_parcel';

    if (validateAvIfmv && !isSingleParcelRun) {
      const missingValueCases = [];
      for (const caseItem of updatedCases) {
        const missing = [];
        if (isMissingNumericValue(getAVUtil(caseItem))) missing.push('assessed value');
        if (isMissingNumericValue(getIFMVUtil(caseItem))) missing.push('IFMV');
        if (missing.length > 0) {
          missingValueCases.push({ label: getCaseLabel(caseItem), missing });
        }
      }

      if (missingValueCases.length > 0) {
        const message = missingValueCases
          .map(({ label, missing }) => `${label}: missing ${missing.join(' and ')}`)
          .join('; ');
        toast.error(`Cannot run model — ${message}`, {
          autoClose: false,
          closeOnClick: true,
          draggable: true,
        });
        return { success: false, reason: 'missing_av_or_ifmv', cases: missingValueCases };
      }
    }

    return { success: true };
  } catch (error) {
    logger.error('❌ Error during Enrich Client Cases fetch:', error);
    return { success: false, reason: 'error', error };
  } finally {
    // Clear loading state -- Do we even need / use this?
    setIsEnrichingClientCases(false);
  }
};